/**
 * BreastPainModule — iter1059 — воден преглед „Болка в гърдата":
 * трите теста за разграничаване, дневник срещу фазите на цикъла,
 * автоматична проверка на енергийния ресурс и предупредителни признаци.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AlertTriangle, ArrowRight, ChevronDown, ClipboardList, Loader2, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import PainCycleChart from "@/components/breast/PainCycleChart";
import RefluxScreenCard from "@/components/breast/RefluxScreenCard";
import ChestWallProgramCard from "@/components/breast/ChestWallProgramCard";

const VERDICT_TONE = {
  chest_wall: "border-amber-400",
  glandular_cyclical: "border-pink-400",
  mixed: "border-violet-400",
  non_cyclical: "border-sky-400",
  unclear: "border-slate-400",
};

const ENERGY_TONE = {
  high: { light: "bg-rose-50 border-rose-200", dark: "bg-rose-900/25 border-rose-500/40" },
  moderate: { light: "bg-amber-50 border-amber-200", dark: "bg-amber-900/20 border-amber-500/30" },
  watch: { light: "bg-sky-50 border-sky-200", dark: "bg-sky-900/20 border-sky-500/30" },
  ok: { light: "bg-emerald-50 border-emerald-200", dark: "bg-emerald-900/20 border-emerald-500/30" },
};

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-1 shrink-0">
    {[["yes", "Да"], ["no", "Не"], ["unsure", "Не знам"]].map(([k, label]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
              className={`text-[10px] px-2 py-1 rounded-md border ${value === k
                ? "bg-pink-600 text-white border-pink-600"
                : "border-slate-300 dark:border-slate-600"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const BreastPainModule = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [why, setWhy] = useState(null);
  const [entry, setEntry] = useState({ intensity: "", side: "left", character: "dull", triggers: [] });
  const [logging, setLogging] = useState(false);

  const load = () => axios.get(`${API}/breast-pain`)
    .then(({ data }) => { setData(data); setAnswers(data.answers || {}); setFlags(data.red_flags?.items?.map((i) => i.key) || []); })
    .catch(() => setData(null))
    .finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const saveAssessment = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/breast-pain/assessment`, { answers, red_flags: flags });
      setData(data);
      toast.success(data.verdict?.label_bg || "Записано.");
    } catch { toast.error("Записът не беше запазен."); }
    finally { setSaving(false); }
  };

  const logPain = async () => {
    setLogging(true);
    try {
      const { data: d } = await axios.post(`${API}/breast-pain/diary`, {
        intensity: entry.intensity === "" ? null : parseFloat(entry.intensity),
        side: entry.side, character: entry.character, triggers: entry.triggers,
      });
      setData((prev) => ({ ...prev, diary: d }));
      setEntry({ ...entry, intensity: "", triggers: [] });
      toast.success("Записано в дневника.");
    } catch { toast.error("Записът не беше запазен."); }
    finally { setLogging(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="breast-pain-loading">
      <Loader2 className="w-5 h-5 animate-spin text-pink-500" /></div>;
  }
  if (!data) return null;
  const et = ENERGY_TONE[data.energy.level] || ENERGY_TONE.ok;

  const question = (q, prefix) => (
    <div key={q.key} className={`rounded-xl overflow-hidden ${box}`}>
      <div className="p-2.5 flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold leading-snug">{q.question_bg}</div>
          {q.how_bg && <div className="text-[10px] opacity-70 mt-0.5 leading-snug">{q.how_bg}</div>}
          <button onClick={() => setWhy(why === q.key ? null : q.key)}
                  className="text-[10px] opacity-50 hover:opacity-100 mt-0.5"
                  data-testid={`${prefix}-why-${q.key}`}>защо?</button>
        </div>
        <YesNo value={answers[q.key]} testid={`${prefix}-${q.key}`}
               onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
      </div>
      {why === q.key && (
        <div className="px-2.5 pb-2.5 text-[10px] opacity-80 leading-relaxed">{q.why_bg}</div>
      )}
    </div>
  );

  return (
    <div className={`p-5 rounded-2xl border-2 ${card}`} data-testid="breast-pain-module">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2"
              data-testid="breast-pain-toggle">
        <Stethoscope className="w-4 h-4 text-pink-500" />
        <h2 className="font-bold text-sm flex-1">Болка в гърдата — воден преглед</h2>
        {data.has_assessment && (
          <span className="text-[10px] px-2 py-1 rounded-md font-bold bg-pink-600 text-white"
                data-testid="breast-pain-verdict-badge">{data.verdict.label_bg}</span>
        )}
        <ChevronDown className={`w-4 h-4 opacity-50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] opacity-75 leading-relaxed mt-1">{data.imaging_bg}</p>

      {data.red_flags.triggered && (
        <div className={`mt-3 p-3 rounded-xl border ${isDark ? "bg-rose-900/30 border-rose-500/50" : "bg-rose-50 border-rose-300"}`}
             data-testid="breast-pain-red-flag-alert">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            <span className="text-xs font-bold">Потърси лекар</span>
          </div>
          <div className="text-[11px] opacity-90 mt-1 leading-relaxed">{data.red_flags.text_bg}</div>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-4">
          {/* 1 — трите теста */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">1. Трите теста за разграничаване</div>
            <div className="space-y-1.5" data-testid="breast-pain-tests">
              {data.tests.map((t) => question(t, "bp-test"))}
            </div>
          </div>

          {/* контекст */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">Контекст, който мени разчитането</div>
            <div className="space-y-1.5" data-testid="breast-pain-context">
              {data.context.map((c) => question(c, "bp-ctx"))}
            </div>
          </div>

          {/* предупредителни признаци */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">Предупредителни признаци — отбележете, ако има</div>
            <div className="flex flex-wrap gap-1.5" data-testid="breast-pain-red-flags">
              {data.red_flags.all_items.map((f) => {
                const on = flags.includes(f.key);
                return (
                  <button key={f.key} data-testid={`bp-flag-${f.key}`}
                          onClick={() => setFlags(on ? flags.filter((k) => k !== f.key) : [...flags, f.key])}
                          className={`text-[10px] px-2 py-1 rounded-lg border text-left ${on
                            ? "bg-rose-600 text-white border-rose-600"
                            : isDark ? "border-slate-700" : "border-slate-300"}`}>
                    {f.label_bg}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={saveAssessment} disabled={saving} data-testid="breast-pain-save"
                  className="w-full py-2 rounded-lg bg-pink-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Разчитам…" : "Разчетете резултата"}
          </button>

          {/* разчитане */}
          {data.has_assessment && (
            <div className={`p-3 rounded-xl border-l-4 ${VERDICT_TONE[data.verdict.verdict] || "border-slate-400"} ${box}`}
                 data-testid="breast-pain-verdict">
              <div className="text-xs font-bold" data-testid="breast-pain-verdict-label">{data.verdict.label_bg}</div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.verdict.text_bg}</div>
              <div className="text-[10px] opacity-55 mt-1.5">
                Гръдна стена {data.verdict.chest_wall_score} · жлеза {data.verdict.glandular_score}
              </div>
              {data.verdict.secondary?.map((s) => (
                <div key={s.key} className="mt-2" data-testid={`breast-pain-secondary-${s.key}`}>
                  <div className="text-[11px] font-bold">{s.label_bg}</div>
                  <div className="text-[10px] opacity-80 leading-relaxed">{s.text_bg}</div>
                </div>
              ))}
            </div>
          )}

          {/* 2 — дневник срещу цикъла */}
          <div>
            <div className="text-[11px] font-bold mb-1.5 flex items-center gap-1">
              <ClipboardList className="w-3.5 h-3.5 text-pink-500" />
              2. Дневник срещу фазите на цикъла
            </div>
            <p className="text-[10px] opacity-70 leading-relaxed mb-2">{data.diary.guidance_bg}</p>

            <div className={`p-2.5 rounded-xl space-y-2 ${box}`} data-testid="breast-pain-diary-form">
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70">Сила 0-10</span>
                  <input type="number" min="0" max="10" className={inputCls} value={entry.intensity}
                         data-testid="bp-diary-intensity"
                         onChange={(e) => setEntry({ ...entry, intensity: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70">Страна</span>
                  <select className={inputCls} value={entry.side} data-testid="bp-diary-side"
                          onChange={(e) => setEntry({ ...entry, side: e.target.value })}>
                    <option value="left">Лява</option>
                    <option value="right">Дясна</option>
                    <option value="both">Двете</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70">Характер</span>
                  <select className={inputCls} value={entry.character} data-testid="bp-diary-character"
                          onChange={(e) => setEntry({ ...entry, character: e.target.value })}>
                    {data.diary.characters.map((c) => (
                      <option key={c.key} value={c.key}>{c.label_bg}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.diary.triggers.map((t) => {
                  const on = entry.triggers.includes(t.key);
                  return (
                    <button key={t.key} data-testid={`bp-diary-trigger-${t.key}`}
                            onClick={() => setEntry({ ...entry,
                              triggers: on ? entry.triggers.filter((k) => k !== t.key) : [...entry.triggers, t.key] })}
                            className={`text-[10px] px-2 py-1 rounded-lg border ${on
                              ? "bg-pink-600 text-white border-pink-600"
                              : isDark ? "border-slate-700" : "border-slate-300"}`}>
                      {t.label_bg}
                    </button>
                  );
                })}
              </div>
              <button onClick={logPain} disabled={logging} data-testid="bp-diary-save"
                      className="w-full py-2 rounded-lg bg-pink-600 text-white text-[11px] font-bold disabled:opacity-60">
                {logging ? "Записвам…" : "Запишете за днес"}
              </button>
            </div>

            {data.diary.needs_cycle_bg && (
              <button onClick={() => navigate("/dashboard/hormonal-twin")}
                      className="text-[10px] opacity-75 hover:opacity-100 underline mt-2 text-left"
                      data-testid="bp-diary-needs-cycle">
                {data.diary.needs_cycle_bg}
              </button>
            )}

            <PainCycleChart chart={data.diary.chart} isDark={isDark} />

            {data.diary.has_data && (
              <div className="mt-2 space-y-2" data-testid="breast-pain-diary-summary">
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <span className={`px-2 py-1 rounded-md ${box}`}>
                    {data.diary.days_logged} дни · средно {data.diary.avg_intensity ?? "—"}/10
                  </span>
                  {Object.entries(data.diary.phase_avg || {}).map(([p, v]) => (
                    <span key={p} className={`px-2 py-1 rounded-md ${box}`} data-testid={`bp-phase-avg-${p}`}>
                      {p}: {v}
                    </span>
                  ))}
                </div>
                {data.diary.pattern && (
                  <div className={`p-3 rounded-xl ${isDark ? "bg-pink-900/20 border border-pink-500/30" : "bg-pink-50 border border-pink-200"}`}
                       data-testid="breast-pain-pattern">
                    <div className="text-xs font-bold" data-testid="breast-pain-pattern-label">{data.diary.pattern.label_bg}</div>
                    <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.diary.pattern.text_bg}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3 — енергиен ресурс */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">3. Енергиен наличен ресурс (автоматично)</div>
            <div className={`p-3 rounded-xl border ${isDark ? et.dark : et.light}`} data-testid="breast-pain-energy">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-bold" data-testid="breast-pain-energy-label">{data.energy.label_bg}</span>
                <span className="ml-auto text-[10px] opacity-60 tabular-nums">{data.energy.points} т.</span>
              </div>
              {data.energy.drivers_bg?.length > 0 && (
                <div className="text-[11px] opacity-85 mt-1" data-testid="breast-pain-energy-drivers">
                  Заради: {data.energy.drivers_bg.join("; ")}.
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2 text-center">
                {[["ИТМ", data.energy.bmi], ["Хранения/ден", data.energy.meals_per_day],
                  ["Крачки", data.energy.avg_steps], ["Акт. мин/сед", data.energy.weekly_active_min]].map(([l, v]) => (
                  <div key={l} className={`p-1.5 rounded-lg ${box}`}>
                    <div className="text-[9px] opacity-60">{l}</div>
                    <div className="text-[11px] font-bold tabular-nums">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] opacity-75 mt-2 leading-relaxed">{data.energy.why_bg}</div>
              <ul className="text-[10px] opacity-85 mt-1.5 space-y-0.5">
                {data.energy.next_bg.map((s, i) => <li key={i}>• {s}</li>)}
              </ul>
              {data.energy.missing_bg?.length > 0 && (
                <div className="text-[10px] opacity-60 mt-1.5" data-testid="breast-pain-energy-missing">
                  Липсва: {data.energy.missing_bg.join("; ")}.
                </div>
              )}
            </div>
          </div>

          {/* 4 — рефлукс и програма за гръдната стена (iter1060) */}
          <RefluxScreenCard isDark={isDark} />
          <ChestWallProgramCard isDark={isDark} />

          {/* план */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">Планът за следващите 4 седмици</div>
            <div className="space-y-1.5" data-testid="breast-pain-plan">
              {data.plan.map((s, i) => (
                <div key={i} className={`p-2.5 rounded-xl ${box}`} data-testid={`bp-plan-${i}`}>
                  <div className="text-[11px] font-bold">{i + 1}. {s.title_bg}</div>
                  <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{s.text_bg}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[10px] opacity-60 leading-relaxed" data-testid="breast-pain-disclaimer">
            {data.disclaimer_bg}
          </p>

          <div className="flex flex-wrap gap-1.5" data-testid="breast-pain-links">
            {data.links.map((l, i) => (
              <button key={i} onClick={() => navigate(l.route)} data-testid={`bp-link-${i}`}
                      className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BreastPainModule;
